// frontend/src/motion/rafCoordinator.js
//
// ONE requestAnimationFrame chain for the whole page.
//
// The homepage prototype ran five independent rAF loops (mesh, cursor ring,
// stream, smoke, dock follow). Five loops are not five times the work, they are
// five uncoordinated wake-ups per frame, each with its own `document.hidden`
// handling to get wrong. This coordinator owns exactly one chain and every
// engine registers a task on it.
//
// It stops the chain ENTIRELY - not "runs and does nothing" - whenever
// `motionSignal` reports `paused` (motion off, or the tab hidden), and restarts
// it when that clears. A stopped coordinator holds no frame callback at all,
// which is the property the accessibility toggle has to be able to promise.
//
// Task contract:
//   fn(dtMs, nowMs)  - dtMs is capped (see DT_CAP_MS) so a task that was
//                      suspended for 40 seconds does not integrate a 40-second
//                      step and teleport. nowMs is the rAF timestamp.
// Tasks run in ascending `priority` (default 0); ties keep insertion order.
// A task that throws is REMOVED, not left to throw once per frame forever.

import { getMotionState, subscribeMotion } from "./motionSignal";

// A tab restored after minutes away hands the first frame a huge delta. Every
// consumer here integrates positions from it, so cap it at ~2.5 frames: motion
// resumes smoothly instead of jumping.
export const DT_CAP_MS = 40;

/** @type {{ fn: Function, priority: number, seq: number }[]} */
let tasks = [];
let seq = 0;

let frameId = 0;
let lastTs = 0;
let subscribed = false;

const now = () =>
  typeof performance !== "undefined" && typeof performance.now === "function"
    ? performance.now()
    : Date.now();

const canRaf = () =>
  typeof window !== "undefined" && typeof window.requestAnimationFrame === "function";

/** Ascending priority, stable within a priority by insertion order. */
function sortTasks() {
  tasks.sort((a, b) => a.priority - b.priority || a.seq - b.seq);
}

function frame(timestamp) {
  frameId = 0;
  const ts = typeof timestamp === "number" ? timestamp : now();
  const dt = lastTs ? Math.min(DT_CAP_MS, ts - lastTs) : 16;
  lastTs = ts;

  // Snapshot: a task may add or remove tasks while running.
  const running = tasks.slice();
  for (const task of running) {
    try {
      task.fn(dt, ts);
    } catch (error) {
      // A decorative loop must never be able to spam the console once per frame
      // or wedge the tasks behind it. Drop it and keep the chain alive.
      removeTask(task.fn);
      if (process.env.NODE_ENV !== "production") {
        // eslint-disable-next-line no-console
        console.error("rafCoordinator: task removed after throwing", error);
      }
    }
  }

  // The last task may have removed itself above. `stop()` could not reset the
  // clock for it - `frameId` is already 0 inside a frame - so do it here, or
  // the next chain would integrate a full DT_CAP_MS step on its first frame.
  if (tasks.length === 0) lastTs = 0;
  schedule();
}

/** Requests the next frame if there is work and motion is allowed. */
function schedule() {
  if (frameId || tasks.length === 0 || !canRaf()) return;
  if (getMotionState().paused) return;
  frameId = window.requestAnimationFrame(frame);
}

function stop() {
  if (frameId) {
    window.cancelAnimationFrame(frameId);
    frameId = 0;
  }
  // Force the next resumed frame to seed its own delta rather than measure the
  // whole pause. Reset even when no frame is outstanding: inside `frame()`
  // frameId is already 0, and a chain that ends there (its last task removing
  // itself) must not hand a stale clock to the next one.
  lastTs = 0;
}

function ensureSubscribed() {
  if (subscribed) return;
  subscribed = true;
  subscribeMotion((state) => {
    if (state.paused) stop();
    else schedule();
  });
}

/**
 * Register a per-frame task.
 *
 * @param {(dtMs: number, nowMs: number) => void} fn
 * @param {{ priority?: number }} [options] lower priority runs first (default 0).
 * @returns {() => void} remove this task.
 */
export function addTask(fn, options = {}) {
  if (typeof fn !== "function") return () => {};
  ensureSubscribed();
  if (!tasks.some((task) => task.fn === fn)) {
    tasks.push({ fn, priority: Number(options.priority) || 0, seq: (seq += 1) });
    sortTasks();
  }
  schedule();
  return () => removeTask(fn);
}

/**
 * Remove a previously registered task. Stops the chain when the last one goes.
 *
 * @param {Function} fn the exact function passed to addTask.
 */
export function removeTask(fn) {
  const before = tasks.length;
  tasks = tasks.filter((task) => task.fn !== fn);
  if (tasks.length !== before && tasks.length === 0) stop();
}

/** @returns {boolean} true while a frame callback is actually outstanding. */
export function isRunning() {
  return frameId !== 0;
}

/** @returns {number} how many tasks are registered. */
export function getTaskCount() {
  return tasks.length;
}

/** Test-only: drop every task and cancel the chain. */
export function __resetRafCoordinatorForTests() {
  tasks = [];
  stop();
  lastTs = 0;
}
